import React, { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
    CircularProgress,
    IconButton,
    makeStyles,
    Typography,
    useTheme,
    Button,
} from '@material-ui/core'
import { Close, Person } from '@material-ui/icons'
import { Pagination } from '@material-ui/lab'

import { PermissionRole, User } from '../../../models'
import { Container } from '../../../components'
import { SearchField } from '../../../components/SearchField'
import { usePagination } from '../../../hooks'
import { BulkArchiveUserRequest } from '../../../hooks/useOrganizationManagement'
import { CompanyTable } from './CompanyTable'
import { typographyStyle } from '../../../theme/typographyStyle'

const useLocalStyles = makeStyles((theme) => ({
    tableWrapper: {
        flex: 1,
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 400px)',
    },

    loadingOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
    },
    stickyHeader: {
        backgroundColor: theme.palette.background.paper,
    },
    table: {
        minWidth: 750,
        tableLayout: 'fixed',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    tableBody: {
        height: 'auto',
    },
    tableRow: {
        height: 48,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    emptySpace: {
        flex: 1,
        minHeight: 0,
    },
}))

interface Props {
    userDetail: User | null
    isLoading: boolean
    organization: number | null
    onClose: () => void
    bulkArchiveUser: (request: BulkArchiveUserRequest) => Promise<void>
    setUserList: Dispatch<SetStateAction<User[]>>
}

export const RemoveUserWorkspaces = (props: Props) => {
    const {
        userDetail,
        isLoading,
        organization,
        onClose,
        bulkArchiveUser,
        setUserList,
    } = props

    const theme = useTheme()
    const localClasses = useLocalStyles()

    const [searchState, setSearchState] = useState<string>('')
    const [selectedCompanies, setSelectedCompanies] = useState<
        Record<string, boolean>
    >({})

    const selectedCount = useMemo(
        () => Object.values(selectedCompanies).filter(Boolean).length,
        [selectedCompanies],
    )

    const handleCompanySelection = (companyId: string, checked: boolean) => {
        setSelectedCompanies((prev) => ({
            ...prev,
            [companyId]: checked,
        }))
    }

    const handleSelectAll = (checked: boolean) => {
        const newSelected: Record<string, boolean> = {}
        userCompanyList.forEach((company) => {
            newSelected[company.id.toString()] = checked
        })
        setSelectedCompanies(newSelected)
    }

    const userCompanyList = useMemo(() => {
        if (!userDetail) return []
        return userDetail.workspaces
            .map((workspaceUser) => workspaceUser.active_workspace)
            .filter((workspace) =>
                workspace.name
                    .toLowerCase()
                    .includes(searchState.toLowerCase()),
            )
    }, [userDetail, searchState])

    const isAllSelected = useMemo(() => {
        return (
            userCompanyList.length > 0 &&
            userCompanyList.every(
                (company) => selectedCompanies[company.id.toString()],
            )
        )
    }, [userCompanyList, selectedCompanies])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        userCompanyList,
    )

    const userCompanyPermissionMap = useMemo(() => {
        if (!userDetail) return new Map<string, PermissionRole[]>()
        return new Map(
            userDetail.workspaces.map((workspaceUser) => [
                workspaceUser.active_workspace.id.toString(),
                workspaceUser.permission_roles.map((role) => role),
            ]),
        )
    }, [userDetail])

    const onClickRemoveUserFromWorkspaces = () => {
        if (!organization || !userDetail) return
        const selectedCompanyIds = Object.keys(selectedCompanies).filter(
            (key) => selectedCompanies[key],
        )
        bulkArchiveUser({
            userId: userDetail.id,
            body: {
                company_ids: selectedCompanyIds.map(Number),
            },
            params: {
                organization: organization ?? undefined,
            },
        }).then(() => {
            toast.success('User removed from workspaces')
            if (selectedCount === userCompanyList.length) {
                setUserList((prev) =>
                    prev.filter((user) => user.id !== userDetail.id),
                )
            }
            onClose()
        })
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(1, 2),
                    borderBottom: `1px solid ${theme.palette.primary.main}`,
                }}
            >
                <Container style={{ flexDirection: 'column' }}>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                        }}
                    >
                        Remove User From Workspaces
                    </span>
                    <Typography
                        variant="subtitle1"
                        style={{
                            marginTop: 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Person fontSize="small" />
                        {userDetail?.name}
                    </Typography>
                </Container>
                <IconButton
                    onClick={onClose}
                    size="small"
                    style={{ marginLeft: 'auto' }}
                >
                    <Close />
                </IconButton>
            </Container>
            {isLoading || !userDetail ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CircularProgress size={50} />
                </Container>
            ) : (
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                    }}
                >
                    <Container style={{ padding: theme.spacing(1, 2) }}>
                        <SearchField
                            style={{ minWidth: 200 }}
                            placeholder="Search properties"
                            value={searchState}
                            onChange={(e) => setSearchState(e.target.value)}
                        />
                    </Container>
                    <CompanyTable
                        companies={pageData}
                        selectedCompanies={selectedCompanies}
                        onSelectCompany={handleCompanySelection}
                        onSelectAll={handleSelectAll}
                        isAllSelected={isAllSelected}
                        selectedCount={selectedCount}
                        userCompanyPermissionMap={userCompanyPermissionMap}
                    />
                    <Container
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            borderTop: `1px solid ${theme.palette.grey[400]}`,
                            padding: theme.spacing(1),
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Container
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Pagination
                                count={numPages}
                                page={page}
                                onChange={(_: unknown, newPage: number) => {
                                    setPage(newPage)
                                }}
                            />
                            <span style={{ marginLeft: theme.spacing(2) }}>
                                {start} - {end} of{' '}
                                {userCompanyList?.length ?? 0}
                            </span>
                        </Container>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={selectedCount === 0}
                            onClick={onClickRemoveUserFromWorkspaces}
                        >
                            Remove from {selectedCount} workspace
                            {selectedCount !== 1 ? 's' : ''}
                        </Button>
                    </Container>
                </Container>
            )}
        </Container>
    )
}
